import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEnvelopeOpen,
  FaShieldAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaUsersCog,
  FaSignature,
} from "react-icons/fa";
import LoadingScreen from "../components/ui/temp-screens/LoadingScreen";
import MailAccountFormModal from "../components/correo/MailAccountFormModal";
import AdminAccessModal from "../components/correo/AdminAccessModal";
import FirmaAccountModal from "../components/correo/FirmaAccountModal";
import Modal from "../components/ui/Modal";
import MainSaveButton from "../components/ui/components/MainSaveButton";
import * as mailService from "../services/mailService";

const AdminCorreo = () => {
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();

  const [mailAccounts, setMailAccounts] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editAccount, setEditAccount] = useState(null);

  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [firmaModalOpen, setFirmaModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user?.area?.toUpperCase() !== "ADMINISTRACION") {
      navigate("/home");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [accountsRes, trabajadoresRes] = await Promise.all([
        mailService.getMailAccounts(),
        mailService.getTrabajadores(),
      ]);
      setMailAccounts(accountsRes.data.docs || []);
      setTrabajadores(trabajadoresRes.data.docs || []);
    } catch (e) {
      toast.error("Error al cargar los datos");
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditAccount(null);
    setFormOpen(true);
  };

  const handleEdit = (account) => {
    setEditAccount(account);
    setFormOpen(true);
  };

  const handleSaveAccount = async (data, accountId) => {
    try {
      if (accountId) {
        await mailService.updateMailAccount(accountId, data);
        toast.success("Cuenta actualizada correctamente");
      } else {
        await mailService.createMailAccount(data);
        toast.success("Cuenta creada correctamente");
      }
      await fetchData();
    } catch (e) {
      const msg = e.response?.data?.errors?.[0]?.message || "Error al guardar la cuenta";
      toast.error(msg);
      throw e;
    }
  };

  const handleManageAccess = (account) => {
    setSelectedAccount(account);
    setAccessModalOpen(true);
  };

  const handleSaveAccess = async (accountId, workerIds) => {
    try {
      await mailService.updateMailAccountAccess(accountId, workerIds);
      setMailAccounts((prev) =>
        prev.map((acc) =>
          acc.id === accountId
            ? { ...acc, trabajadoresConAcceso: workerIds.map((id) => ({ id })) }
            : acc
        )
      );
      toast.success("Accesos actualizados correctamente");
    } catch (e) {
      toast.error("Error al actualizar los accesos");
      console.log(e);
      throw e;
    }
  };

  const handleEditFirma = (account) => {
    setSelectedAccount(account);
    setFirmaModalOpen(true);
  };

  const handleSaveFirma = async (accountId, firma) => {
    try {
      await mailService.updateMailAccountFirma(accountId, firma);
      setMailAccounts((prev) =>
        prev.map((acc) => (acc.id === accountId ? { ...acc, firma } : acc))
      );
      toast.success("Firma guardada correctamente");
    } catch (e) {
      toast.error("Error al guardar la firma");
      console.log(e);
      throw e;
    }
  };

  const handleDeleteClick = (account) => {
    setDeleteTarget(account);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await mailService.deleteMailAccount(deleteTarget.id);
      setMailAccounts((prev) => prev.filter((a) => a.id !== deleteTarget.id));
      toast.success("Cuenta eliminada correctamente");
      setDeleteOpen(false);
      setDeleteTarget(null);
    } catch (e) {
      toast.error("Error al eliminar la cuenta");
      console.log(e);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <FaShieldAlt className="text-orange-500" />
          <h1 className="text-lg font-bold text-gray-800">Administración de correo</h1>
        </div>
        <LoadingScreen text="Cargando cuentas y trabajadores..." />
      </div>
    );
  }

  return (
    <div className="">

      {/* NavBar */}
      <div className="flex flex-row px-4 py-2 bg-gray-100">
        <div className="flex flex-row w-full items-center gap-4 justify-between overflow-x-auto overflow-y-hidden px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex items-center gap-4 py-4">
            <FaShieldAlt className="text-zinc-700 text-2xl" />
            <h2 className="text-2xl font-bold text-zinc-700">Administración de correo</h2>
          </div>
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs font-bold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors shadow-sm"
          >
            <FaPlus className="text-[10px]" />
            Nueva cuenta
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <FaEnvelopeOpen className="text-orange-500 text-sm" />
            <h2 className="text-sm font-bold text-gray-700">Cuentas registradas</h2>
          </div>
          <span className="text-[11px] text-gray-400 font-semibold">
            {mailAccounts.length} cuenta(s)
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-2.5 text-[11px] font-bold text-gray-600 uppercase tracking-wider">Nombre</th>
                <th className="px-4 py-2.5 text-[11px] font-bold text-gray-600 uppercase tracking-wider">Email</th>
                <th className="px-4 py-2.5 text-[11px] font-bold text-gray-600 uppercase tracking-wider text-center">Estado</th>
                <th className="px-4 py-2.5 text-[11px] font-bold text-gray-600 uppercase tracking-wider text-center">Trabajadores</th>
                <th className="px-4 py-2.5 text-[11px] font-bold text-gray-600 uppercase tracking-wider text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {mailAccounts.map((acc) => {
                const workerCount = acc.trabajadoresConAcceso?.length || 0;
                const isActive = acc.activo !== false;
                return (
                  <tr key={acc.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold text-gray-800">
                        {acc.nombre || "Sin nombre"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">{acc.imapUser}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isActive ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-[11px] font-semibold rounded-full">
                          <FaCheckCircle className="text-[9px]" />
                          Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 text-[11px] font-semibold rounded-full">
                          <FaTimesCircle className="text-[9px]" />
                          Inactivo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-xs text-gray-600 font-semibold">{workerCount}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1.5 flex-wrap">
                        <button
                          onClick={() => handleEdit(acc)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors"
                        >
                          <FaEdit className="text-xs" />
                          Editar
                        </button>
                        <button
                          onClick={() => handleManageAccess(acc)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                          <FaUsersCog className="text-xs" />
                          Accesos
                        </button>
                        <button
                          onClick={() => handleEditFirma(acc)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          <FaSignature className="text-xs" />
                          Firma
                        </button>
                        <button
                          onClick={() => handleDeleteClick(acc)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          <FaTrash className="text-xs" />
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {mailAccounts.length === 0 && (
            <div className="text-center py-12 text-xs text-gray-400">
              No hay cuentas de correo registradas. Haz clic en "Nueva cuenta" para crear una.
            </div>
          )}
        </div>
      </div>

      <MailAccountFormModal
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditAccount(null);
        }}
        account={editAccount}
        trabajadores={trabajadores}
        onSave={handleSaveAccount}
      />

      <AdminAccessModal
        open={accessModalOpen}
        onClose={() => setAccessModalOpen(false)}
        account={selectedAccount}
        trabajadores={trabajadores}
        onSave={handleSaveAccess}
      />

      <FirmaAccountModal
        open={firmaModalOpen}
        onClose={() => setFirmaModalOpen(false)}
        account={selectedAccount}
        onSave={handleSaveFirma}
      />

      <Modal
        activeModal={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeleteTarget(null);
        }}
        title="Confirmar eliminación"
        className="max-w-md"
        themeClass="bg-red-500"
        centered
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700">
            ¿Estás seguro de que deseas eliminar la cuenta{" "}
            <span className="font-bold">{deleteTarget?.nombre || deleteTarget?.imapUser}</span>?
          </p>
          <p className="text-xs text-gray-500">
            Esta acción no se puede deshacer. Los trabajadores que tenían acceso a esta cuenta
            dejarán de poder utilizarla.
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => {
                setDeleteOpen(false);
                setDeleteTarget(null);
              }}
              className="px-4 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <MainSaveButton
              text="Eliminar cuenta"
              onClick={handleDeleteConfirm}
              loading={deleting}
              icon={<FaTrash />}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminCorreo;
